/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-09-24 20:41:20
 */

import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react'
import { Button, Checkbox, Spin, Switch, Tag, Tree } from 'antd'
import produce from 'immer'
import sensors from '../../../../utils/sensors'
import {
  useAppDispatch,
  useAppSelector,
  useGroupID
} from '../../../../app/hooks'
import { searchDepartmentMember } from '../../../../actions/group'
import { DepartmentMemberV2 } from '../../../../global'

const { TreeNode } = Tree

interface ITitle {
  user: DepartmentMemberV2
  selected: boolean
  selectedDepartmentUsers: DepartmentMemberV2[]
  setSelectedDepartmentUsers: (
    selectedDepartmentUsers: DepartmentMemberV2[]
  ) => void
  removeUserFromSelectedUsers: (userID: string) => void
  searchValue: string
}

const Title: React.FC<ITitle> = (props) => {
  const {
    user,
    selected,
    selectedDepartmentUsers,
    setSelectedDepartmentUsers,
    removeUserFromSelectedUsers,
    searchValue
  } = props
  const groupID = useGroupID()
  return (
    <span className="treenode-title">
      <span className="name">
        {user.quit && <Tag>已退出</Tag>}
        {user.name}
      </span>
      <Checkbox
        checked={selected}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedDepartmentUsers(
              produce(selectedDepartmentUsers, (draft) => {
                if (!selected) {
                  draft.push(user)
                }
              })
            )
            sensors.track('web_workgroup_filter_member_page_click', {
              clickItem: 'selectOneMember',
              memberStatus: user.quit ? 'offJob' : 'onJob',
              pagePath: window.location.pathname,
              groupID: groupID,
              content: searchValue
            })
          } else {
            removeUserFromSelectedUsers(user.userID)
            sensors.track('web_workgroup_filter_member_page_click', {
              clickItem: 'unSelectOneMember',
              memberStatus: user.quit ? 'offJob' : 'onJob',
              pagePath: window.location.pathname,
              groupID: groupID
            })
          }
        }}
      />
    </span>
  )
}

interface IUserTreeNode {
  user: DepartmentMemberV2
  selectedDepartmentUsers: DepartmentMemberV2[]
  setSelectedDepartmentUsers: (
    selectedDepartmentUsers: DepartmentMemberV2[]
  ) => void
  removeUserFromSelectedUsers: (userID: string) => void
  searchValue: string
}

const UserTreeNode: React.FC<IUserTreeNode> = (props) => {
  const {
    user,
    selectedDepartmentUsers,
    setSelectedDepartmentUsers,
    removeUserFromSelectedUsers,
    searchValue
  } = props
  const selected = selectedDepartmentUsers.some(
    (selectedUser) => selectedUser.userID === user.userID
  )
  return (
    <TreeNode
      icon={
        <img
          className="headimg"
          src={user.avatar + '?x-oss-process=image/resize,w_48'}
        />
      }
      title={
        <Title
          user={user}
          selected={selected}
          selectedDepartmentUsers={selectedDepartmentUsers}
          setSelectedDepartmentUsers={setSelectedDepartmentUsers}
          removeUserFromSelectedUsers={removeUserFromSelectedUsers}
          searchValue={searchValue}
        />
      }
    />
  )
}

// “Tree only accept TreeNode as children.” rc-tree逻辑，绕过这个限制
;(UserTreeNode as any).isTreeNode = true

interface ISearchMemberResult {
  searchName: string
  selectedDepartmentUsers: DepartmentMemberV2[]
  setSelectedDepartmentUsers: (
    selectedDepartmentUsers: DepartmentMemberV2[]
  ) => void
  removeUserFromSelectedUsers: (userID: string) => void
  searchValue: string
}

const SearchMemberResult = (props: ISearchMemberResult, ref) => {
  const {
    searchName,
    selectedDepartmentUsers,
    setSelectedDepartmentUsers,
    removeUserFromSelectedUsers,
    searchValue
  } = props
  const [users, setUsers] = useState<DepartmentMemberV2[]>([])
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup) as
    | 0
    | 1
    | 2
  const searchDepartmentUser = (searchName: string) => {
    setLoading(true)
    dispatch(searchDepartmentMember({ searchKey: searchName })).then((res) => {
      setUsers(res.member)
      setLoading(false)
    })
  }

  useImperativeHandle(ref, () => ({
    selectAllUsers
  }))

  const selectAllUsers = () => {
    setSelectedDepartmentUsers(selectedDepartmentUsers.concat(users))
  }

  useEffect(() => {
    if (searchName) {
      searchDepartmentUser(searchName)
    }
  }, [searchName, isOnlyInGroup])

  if (loading) {
    return (
      <div className="search-member-loading">
        <Spin spinning={users.length === 0} />
      </div>
    )
  }

  const treeData = users.map((user) => {
    const selected = selectedDepartmentUsers.some(
      (selectedUser) => selectedUser.userID === user.userID
    )
    return {
      key: user.userID,
      isLeaf: true,
      icon: (
        <img
          className="headimg"
          src={user.avatar + '?x-oss-process=image/resize,w_48'}
        />
      ),
      title: (
        <Title
          user={user}
          selected={selected}
          selectedDepartmentUsers={selectedDepartmentUsers}
          setSelectedDepartmentUsers={setSelectedDepartmentUsers}
          removeUserFromSelectedUsers={removeUserFromSelectedUsers}
          searchValue={searchValue}
        />
      )
    }
  })

  return (
    <Tree
      showIcon
      blockNode
      multiple={false}
      selectable={false}
      treeData={treeData}
    />
  )
}

export default forwardRef(SearchMemberResult)
